import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
} from "@heroicons/react/16/solid";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { api } from "../../../api";
import { SegmentFiltersTooltip } from "../../../externalComponents/SegmentFiltersTooltip";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import { Link } from "../../../externalComponents/tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { Text } from "../../../externalComponents/tailwind/text";
import CancelStoppedCampaignDialog from "./CancelStoppedCampaignDialog";
import ResumeCampaignDialog from "./ResumeCampaignDialog";

const StoppedSendings = forwardRef(
  ({ user, isMobile, onScheduledRefresh }, ref) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [stoppedBatches, setStoppedBatches] = useState([]);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [refreshing, setRefreshing] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [batchToCancel, setBatchToCancel] = useState(null);
    const itemsPerPage = 5;

    const fetchStoppedBatches = async (resetPage = false) => {
      if (resetPage) {
        setCurrentPage(1);
      }
      try {
        const result = await api.view(
          `($customerId: String) {
          sendings {
            sendingBatchId
            bucket: count(1)
            [where customerId == $customerId && status == "stopped" && sendingBatchId != null group by sendingBatchId]
          }
        }`,
          { customerId: user.customerId },
        );

        const batchIds = result?.sendings?.map((s) => s.sendingBatchId) || [];

        const enrichedBatches = await Promise.all(
          batchIds.map(async (batchId) => {
            let sendings = [];
            let tmp = await api.sending.findMany({
              first: 250,
              filter: {
                customerId: { equals: user.customerId },
                sendingBatchId: { equals: batchId },
                status: { equals: "stopped" },
              },
              select: {
                id: true,
                sendingDate: true,
                contactList: true,
                campaign: { name: true, id: true },
                segment: { name: true },
                segmentFilters: true,
                contactListSegment: {
                  externalId: true,
                  type: true,
                },
              },
              sort: { sendingDate: "Ascending" },
            });

            sendings = sendings.concat(tmp);

            while (tmp.hasNextPage) {
              tmp = await tmp.nextPage();
              sendings = sendings.concat(tmp);
            }

            if (sendings.length === 0) return null;

            return {
              sendingBatchId: batchId,
              campaignName: sendings[0].campaign?.name || "-",
              campaignId: sendings[0].campaign?.id,
              segmentName: sendings[0].segment?.name || "-",
              segmentFilters: sendings[0].segmentFilters,
              contactListSegment: sendings[0].contactListSegment,
              originalSendingDate: new Date(sendings[0].sendingDate),
              totalContacts: sendings.reduce(
                (sum, s) => sum + (s.contactList?.length || 0),
                0,
              ),
            };
          }),
        );

        const validBatches = enrichedBatches
          .filter((b) => b !== null)
          .sort((a, b) => a.originalSendingDate - b.originalSendingDate);

        setStoppedBatches(validBatches);
      } catch (error) {
        console.error("Error fetching stopped batches:", error);
        setStoppedBatches([]);
      }
    };

    const handleResumeClick = (batch) => {
      setSelectedBatch(batch);
      setShowResumeModal(true);
    };

    const handleCancelClick = (batch) => {
      setBatchToCancel(batch);
      setShowCancelModal(true);
    };

    const handleCancelSuccess = (sendingBatchId) => {
      setStoppedBatches((prev) =>
        prev.map((b) =>
          b.sendingBatchId === sendingBatchId
            ? { ...b, status: "canceled" }
            : b,
        ),
      );
      setBatchToCancel(null);
    };

    const handleCancelModalClose = () => {
      setShowCancelModal(false);
      setBatchToCancel(null);
    };

    const handleResumeSuccess = async () => {
      await fetchStoppedBatches(true);
    };

    const handleResumeModalClose = () => {
      setShowResumeModal(false);
      setSelectedBatch(null);
    };

    const handleRefresh = async () => {
      setRefreshing(true);
      await fetchStoppedBatches(true);
      setRefreshing(false);
    };

    useImperativeHandle(ref, () => ({
      refresh: () => fetchStoppedBatches(true),
    }));

    useEffect(() => {
      if (user?.customerId) {
        fetchStoppedBatches();
      }
    }, [user?.customerId]);

    const totalPages = Math.ceil(stoppedBatches.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedBatches = stoppedBatches.slice(startIndex, endIndex);

    const getNextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };

    const getPreviousPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const rowMarkup = paginatedBatches.map((batch, index) => {
      const isCancelled = batch.status === "canceled";
      return (
        <TableRow key={`batch-${batch.sendingBatchId}`} position={index}>
          <TableCell className="font-bold">
            {batch.campaignName}
            {isCancelled && (
              <span className="ml-2 text-red-600 text-sm">(Cancelled)</span>
            )}
          </TableCell>
          <TableCell>
            {batch.originalSendingDate.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              year: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            }) + ` [${timezone}]`}
          </TableCell>
          <TableCell className="text-center">{batch.totalContacts}</TableCell>
          <TableCell className="w-[30%]">
            {batch.segmentFilters ? (
              <SegmentFiltersTooltip segmentFilters={batch.segmentFilters}>
                <span className="cursor-help">{batch.segmentName}</span>
              </SegmentFiltersTooltip>
            ) : batch.contactListSegment?.type === "klaviyo-segment" ? (
              <Link
                style={{
                  wordBreak: "break-all",
                  overflowWrap: "break-word",
                }}
                className="hover:underline"
                href={`https://www.klaviyo.com/list/${batch.contactListSegment?.externalId}`}
                target="_blank"
              >
                {`https://www.klaviyo.com/list/${batch.contactListSegment?.externalId}`}
              </Link>
            ) : (
              batch.segmentName
            )}
          </TableCell>
          {!isMobile && (
            <TableCell className="text-center">
              {!isCancelled ? (
                <div className="flex gap-2 justify-center">
                  <Button
                    color="secondaryButton"
                    className="min-w-[100px] h-10"
                    onClick={() => handleResumeClick(batch)}
                  >
                    Resume
                  </Button>
                  <Button
                    color="red"
                    className="min-w-[100px] h-10"
                    onClick={() => handleCancelClick(batch)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <span className="text-gray-400 text-sm">-</span>
              )}
            </TableCell>
          )}
        </TableRow>
      );
    });

    return (
      <>
        <br />
        <div className="flex items-center gap-3 mb-4">
          <Text className="text-xl font-bold">Stopped campaigns</Text>
          <Button
            color="secondaryButton"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-8 w-8 p-0 flex items-center justify-center"
            title="Refresh"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
        {stoppedBatches.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Campaign name</TableHeader>
                  <TableHeader>First stopped date</TableHeader>
                  <TableHeader className="text-center">
                    Remaining contacts
                  </TableHeader>
                  <TableHeader className="w-[30%]">Segment</TableHeader>
                  {!isMobile && (
                    <TableHeader className="text-center">Action</TableHeader>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>{rowMarkup}</TableBody>
            </Table>
            {stoppedBatches.length > itemsPerPage && (
              <div className="flex justify-start my-4">
                <Button
                  color="secondaryButton"
                  onClick={getPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ArrowLeftIcon />
                  Previous
                </Button>
                <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
                  {currentPage}
                </Badge>
                <Button
                  color="secondaryButton"
                  onClick={getNextPage}
                  disabled={currentPage >= totalPages}
                >
                  Next
                  <ArrowRightIcon />
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState heading="There are no stopped campaigns" />
        )}

        <ResumeCampaignDialog
          isOpen={showResumeModal}
          onClose={handleResumeModalClose}
          selectedBatch={selectedBatch}
          user={user}
          onSuccess={handleResumeSuccess}
          onScheduledRefresh={onScheduledRefresh}
        />

        <CancelStoppedCampaignDialog
          isOpen={showCancelModal}
          onClose={handleCancelModalClose}
          batchToCancel={batchToCancel}
          user={user}
          onSuccess={handleCancelSuccess}
        />
      </>
    );
  },
);

StoppedSendings.displayName = "StoppedSendings";

export default StoppedSendings;
